import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X, Trash2, ImagePlus, Coins, Puzzle } from "lucide-react";

const DENOMS = ["pp", "gp", "sp", "cp"];

export default function ChestInspector({ gameId, token, onClose, onDelete, onSwapImage }) {
  const [name, setName] = useState(token.name || "");
  const [addCoins, setAddCoins] = useState({ pp: "", gp: "", sp: "", cp: "" });
  const [puzzleType, setPuzzleType] = useState(token.puzzle_lock?.type || "none");
  const [puzzleItems, setPuzzleItems] = useState((token.puzzle_lock?.required_item_names || []).join(", "));
  const [puzzleQuestion, setPuzzleQuestion] = useState(token.puzzle_lock?.riddle_question || "");
  const [puzzleAnswer, setPuzzleAnswer] = useState(token.puzzle_lock?.riddle_answer || "");

  const patch = (updates) => api.patch(`/games/${gameId}/tokens/${token.id}`, updates);

  const savePuzzleLock = async () => {
    const puzzleLock = puzzleType === "none" ? null : puzzleType === "riddle"
      ? { type: "riddle", riddle_question: puzzleQuestion.trim(), riddle_answer: puzzleAnswer.trim(), solved: token.puzzle_lock?.solved || false }
      : { type: "items", required_item_names: puzzleItems.split(",").map((s) => s.trim()).filter(Boolean), solved: token.puzzle_lock?.solved || false };
    await patch({ puzzle_lock: puzzleLock });
    toast.success("Puzzle lock saved");
  };

  const removeLootItem = async (idx) => {
    const loot = (token.loot || []).filter((_, i) => i !== idx);
    await patch({ loot });
  };

  const currentCoins = token.chest_currency || { cp: 0, sp: 0, gp: 0, pp: 0 };
  const hasCoins = DENOMS.some((d) => currentCoins[d]);

  const submitAddCoins = async () => {
    const next = { ...currentCoins };
    let any = false;
    for (const d of DENOMS) {
      const amt = parseInt(addCoins[d], 10) || 0;
      if (amt > 0) {
        next[d] = (next[d] || 0) + amt;
        any = true;
      }
    }
    if (!any) return toast.error("Type an amount in at least one coin field");
    await patch({ chest_currency: next });
    setAddCoins({ pp: "", gp: "", sp: "", cp: "" });
    toast.success("Coins added to the chest");
  };

  return (
    <div className="absolute bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-20 w-[92vw] max-w-md max-h-[70vh] overflow-y-auto scrollbar-thin bg-black/70 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 space-y-3" data-testid="chest-inspector">
      <div className="flex items-center justify-between">
        <Input
          data-testid="chest-inspector-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => patch({ name })}
          className="bg-black/30 border-white/10 h-8 text-sm"
        />
        <button data-testid="chest-inspector-close" onClick={onClose} className="ml-2 text-[#f1e0c5]/40 hover:text-[#D4AF37]">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Items Inside ({(token.loot || []).length})</p>
        {(token.loot || []).length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">Empty — add item cards from the Rewards tab's "Add to Loot".</p>}
        <div className="space-y-1">
          {(token.loot || []).map((item, idx) => (
            <div key={idx} data-testid={`chest-inspector-loot-item-${idx}`} className="flex items-center justify-between bg-black/20 rounded-lg px-2.5 py-1.5">
              <span className="text-xs capitalize">{item.name} <span className="text-[#f1e0c5]/30">({item.rarity})</span></span>
              <button data-testid={`chest-inspector-remove-loot-${idx}`} onClick={() => removeLootItem(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Coins className="w-3 h-3 text-[#D4AF37]" /> Coins</p>
        {hasCoins && (
          <p className="text-xs text-[#D4AF37]" data-testid="chest-inspector-current-coins">
            Currently holds: {DENOMS.filter((d) => currentCoins[d]).map((d) => `${currentCoins[d]} ${d}`).join(", ")}
          </p>
        )}
        <div className="grid grid-cols-4 gap-1.5">
          {DENOMS.map((d) => (
            <div key={d} className="space-y-1">
              <p className="text-[9px] uppercase text-[#f1e0c5]/30 text-center">{d}</p>
              <Input
                data-testid={`chest-inspector-add-${d}-input`}
                type="number"
                min={0}
                value={addCoins[d]}
                onChange={(e) => setAddCoins({ ...addCoins, [d]: e.target.value })}
                placeholder="0"
                className="bg-black/30 border-white/10 h-8 text-xs text-center"
              />
            </div>
          ))}
        </div>
        <Button data-testid="chest-inspector-add-coins-button" onClick={submitAddCoins} size="sm" variant="secondary" className="w-full rounded-full">Add Coins to Chest</Button>
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 flex items-center gap-1.5"><Puzzle className="w-3 h-3 text-[#D4AF37]" /> Puzzle Lock</p>
        {token.puzzle_lock?.solved && <p className="text-xs text-[#4d7c0f]" data-testid="chest-inspector-puzzle-solved">Already solved — chest is loot-able.</p>}
        <Select value={puzzleType} onValueChange={setPuzzleType}>
          <SelectTrigger data-testid="chest-inspector-puzzle-type-select" className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="items">Requires Items (from inventory)</SelectItem>
            <SelectItem value="riddle">Riddle Answer</SelectItem>
          </SelectContent>
        </Select>
        {puzzleType === "items" && (
          <Input data-testid="chest-inspector-puzzle-items-input" value={puzzleItems} onChange={(e) => setPuzzleItems(e.target.value)} placeholder="Required item names, comma-separated" className="bg-black/30 border-white/10 h-8 text-xs" />
        )}
        {puzzleType === "riddle" && (
          <>
            <Input data-testid="chest-inspector-puzzle-question-input" value={puzzleQuestion} onChange={(e) => setPuzzleQuestion(e.target.value)} placeholder="Riddle shown to players" className="bg-black/30 border-white/10 h-8 text-xs" />
            <Input data-testid="chest-inspector-puzzle-answer-input" value={puzzleAnswer} onChange={(e) => setPuzzleAnswer(e.target.value)} placeholder="Answer" className="bg-black/30 border-white/10 h-8 text-xs" />
          </>
        )}
        <Button data-testid="chest-inspector-save-puzzle-button" onClick={savePuzzleLock} size="sm" variant="secondary" className="w-full rounded-full">Save Puzzle Lock</Button>
      </div>

      <div className="flex gap-2 pt-1">
        <Button data-testid="chest-inspector-swap-image" size="sm" variant="secondary" className="rounded-full gap-1.5 flex-1" onClick={onSwapImage}>
          <ImagePlus className="w-3.5 h-3.5" /> Swap Image
        </Button>
        <Button data-testid="chest-inspector-delete" size="sm" className="rounded-full gap-1.5 bg-[#D90429] hover:bg-[#b8031f]" onClick={onDelete}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
